import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSession } from '@/auth/Session';
import { useAppConfigContext } from '@/context/AppConfig';
import { Button, Card, Input, Text, Grid, useTheme } from '@/theme/index';
import { schemaReferralForm } from '@/utils/schemaForms/schemaForms';
import { ValidationErrorMessage } from '../../../auth/components';
import { ReferralConfirmation } from '../ConfirmationModal/ReferralConfirmation';
import { ReferralsHeader } from '../Header/ReferralsHeader';
import { sendReferral } from '../Referral';

export function ReferralForm() {
  const {
    session: { account },
    setSession,
  } = useSession();

  const { logError } = useAppConfigContext();
  const theme = useTheme();
  const [invitesSent, setInviteCount] = useState(
    account?.account.user_info.invite_count || 0
  );
  const [openModal, setOpenModal] = useState(false);
  const [isEmailTaken, setEmailTaken] = useState(false);
  const [emailReferred, setEmailReferred] = useState('');

  useEffect(() => {
    setInviteCount(account?.account.user_info.invite_count || 0);
  }, [account]);

  const { handleSubmit, register, formState, getFieldState, reset } = useForm<{
    email: string;
  }>({
    resolver: yupResolver(schemaReferralForm),
    defaultValues: { email: '' },
  });
  const useSendReferral = useMutation(sendReferral, {
    onSuccess: () => {
      setOpenModal(true);
    },
    onError: (error: Error) => {
      setEmailTaken(true);
      logError('ResetPasswordRequestForm', error);
    },
  });

  const closeModal = async () => {
    reset();
    setOpenModal(false);
    const newInvitesCount = invitesSent + 1;
    setInviteCount(newInvitesCount);
    account!.account.user_info.invite_count = newInvitesCount;
    setSession({ account });
  };

  return (
    <>
      <ReferralConfirmation
        closeModal={closeModal}
        open={openModal}
        email={emailReferred}
      />
      <ReferralsHeader inviteCount={invitesSent} />
      {invitesSent < 3 ? (
        <form
          id="send-referral-form"
          onSubmit={handleSubmit((data) => {
            setEmailTaken(false);
            setEmailReferred(data.email);
            useSendReferral.mutate({ email: data.email });
          })}
          autoComplete="off"
        >
          <Card
            variant="outlined"
            sx={{ backgroundColor: theme.palette.grey[200] }}
          >
            <Grid width="100%" alignItems="center">
              <Text variant="h2" align="center">
                {invitesSent}/3 invites sent
              </Text>
            </Grid>
            <Grid container direction="row" spacing={2} alignItems="center">
              <Grid item md={9} sm={8} xs={12}>
                <Input
                  {...register('email')}
                  placeholder="Enter an email address"
                  fullWidth
                  type="text"
                  id="email"
                  name="email"
                  error={getFieldState('email').invalid}
                />
                {formState.errors.email?.message && (
                  <ValidationErrorMessage>
                    {formState.errors.email?.message}
                  </ValidationErrorMessage>
                )}
                {isEmailTaken && (
                  <ValidationErrorMessage>
                    This email address is already invited
                  </ValidationErrorMessage>
                )}
              </Grid>
              <Grid item md={3} sm={4} xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  Send invite
                </Button>
              </Grid>
            </Grid>
          </Card>
        </form>
      ) : null}
    </>
  );
}
